import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIeKitLabels, useRestrictedTagLabel } from '@/hooks/useIeKitLabels';
import { useModuleConfig } from '@/hooks/queries/admin/useModuleConfig';
import type { IemModuleConfig } from '@/types/moduleConfig';
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useQueryClient } from '@tanstack/react-query';
import type { Entity } from '@/types/Entity';
import { useEntities } from '@/hooks';
import { entityService } from '@/services/EntityService';
import { iemService } from '@/services/iem';
import { useNotifications } from '@/stores/useNotificationStore';
import { useWriteGuard } from '@/hooks/useWriteGuard';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/useAuthStore';
import { getActorIdFromToken } from '@/utils/jwtUtils';
import {
  DualPaneSelectionDialog,
  SelectionItem,
} from '@/components/shared/DualPaneSelectionDialog';
import { IeImportDialog } from './IeImportDialog';
import { exportIeKitToXlsx, type IEExportRow } from '@/utils/ieExport';
import { multiWordMatch } from '@/utils/multiWordMatch';
import { usePersonLabels } from '@/hooks/usePersonLabels';

interface IeKitEditorDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  kit?: Entity | null;
  onClose: () => void;
  onSaved?: (kit: Entity) => void;
}

interface KitItemDraft {
  catalogId: string;
  quantity: number;
  /** display_name from the ie_kit_item entity, used as fallback when catalog is missing */
  kitItemDisplayName?: string;
  /** entity_identifier from the ie_kit_item entity (e.g. IE-KIT::CAT-REF) */
  kitItemIdentifier?: string;
}

interface KitItemEntityData {
  catalog_id?: string;
  quantity?: number;
  ie_kit_id?: string;
}

interface KitEntityData {
  name?: string;
  soc_tag?: boolean;
}

// --- Helpers ---

const normalizeCatalogText = (entity: Entity): string => {
  const fields: string[] = [entity.display_name ?? '', entity.entity_identifier ?? ''];
  const data = (entity.entity_data ?? {}) as Record<string, unknown>;
  ['lin', 'nsn', 'name'].forEach((k) => {
    if (typeof data[k] === 'string') fields.push(data[k] as string);
  });
  return fields.join(' ').trim().toLowerCase();
};

const buildCatalogSubtitle = (entity: Entity | undefined, draft?: KitItemDraft): string => {
  if (!entity) {
    const ident = draft?.kitItemIdentifier ?? '';
    const catalogRef = ident.includes('::') ? ident.split('::').pop()! : ident;
    return catalogRef;
  }
  const data = (entity.entity_data ?? {}) as Record<string, unknown>;
  const parts = [data.lin, data.nsn].filter(Boolean);
  return parts.join(' • ') || 'Catalog Item';
};

const buildCatalogPrimaryText = (
  entity: Entity | undefined,
  draft?: KitItemDraft,
): React.ReactNode => {
  if (!entity) {
    // display_name is stored as "Kit Name • Catalog Name"; extract the catalog portion
    const raw = draft?.kitItemDisplayName ?? '';
    const catalogName = raw.includes(' • ') ? raw.split(' • ').slice(1).join(' • ') : raw;
    return catalogName ? `⚠ Catalog item not found (${catalogName})` : '⚠ Catalog item not found';
  }
  const data = (entity.entity_data ?? {}) as Record<string, unknown>;
  const isBuyProgram = typeof data.type === 'string' && data.type.toLowerCase() === 'buy program';

  if (isBuyProgram) {
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span>{entity.display_name}</span>
        <Chip label="Buy Program" size="small" color="primary" />
      </span>
    );
  }

  return entity.display_name;
};

const getKitItemQuantity = (entity: Entity): number => {
  const val = (entity.entity_data as KitItemEntityData)?.quantity;
  const num = Number(val);
  return Number.isFinite(num) && num > 0 ? Math.round(num) : 1;
};

export const IeKitEditorDialog: React.FC<IeKitEditorDialogProps> = ({
  open,
  mode,
  kit,
  onClose,
  onSaved,
}) => {
  const isViewer = useWriteGuard();
  const { isComptroller, isHr } = useAuth();
  const person = usePersonLabels('front_window');
  const { label: ieKitLabel } = useIeKitLabels('front_window');
  const restrictedTagLabel = useRestrictedTagLabel();
  const { config: iemConfig } = useModuleConfig<IemModuleConfig>('iem');
  const ieKitIdPrefix = iemConfig.features.ie_kits?.ie_kit_id_prefix || 'IE';
  const [kitName, setKitName] = useState('');
  const [kitItems, setKitItems] = useState<Record<string, KitItemDraft>>({});
  const [kitItemsInitialized, setKitItemsInitialized] = useState(false);
  const [availableSearch, setAvailableSearch] = useState('');
  const [kitSearch, setKitSearch] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [kitNameTouched, setKitNameTouched] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmRemoveItem, setConfirmRemoveItem] = useState<SelectionItem | null>(null);
  const [qtyDecreaseWarning, setQtyDecreaseWarning] = useState<{ affectedCount: number } | null>(
    null,
  );
  const [assignedMemberCount, setAssignedMemberCount] = useState<number | null>(null);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [kitRestrictedTag, setKitRestrictedTag] = useState(false);

  const queryClient = useQueryClient();
  const notifications = useNotifications();

  // --- Data Fetching ---

  const {
    data: catalogItems = [],
    isLoading: catalogLoading,
    error: catalogError,
  } = useEntities({
    type: 'catalog',
    filters: {
      status: 'eq.active',
      'entity_data->>parent_id': 'is.null',
      or: '(entity_data->>type.is.null,entity_data->>type.neq.pnp)',
    },
    limit: 1000,
    order: 'display_name.asc',
    enabled: open,
  });

  const kitId = kit?.id ?? null;
  const { data: existingKitItems = [], isLoading: kitItemsLoading } = useEntities({
    type: 'ie_kit_item',
    filters: kitId ? { 'entity_data->>ie_kit_id': `eq.${kitId}` } : undefined,
    limit: 500,
    enabled: open && mode === 'edit' && !!kitId,
  });

  // --- State Management ---

  useEffect(() => {
    if (!open) return;

    const initialName = (kit?.entity_data as KitEntityData)?.name || kit?.display_name || '';
    setKitName(mode === 'create' ? '' : initialName);
    setKitRestrictedTag(
      mode === 'create' ? false : Boolean((kit?.entity_data as KitEntityData)?.soc_tag),
    );
    setAvailableSearch('');
    setKitSearch('');
    setErrorMessage(null);
    setKitNameTouched(false);

    setAssignedMemberCount(null);

    if (mode === 'create') {
      setKitItems({});
      setKitItemsInitialized(true);
    } else {
      setKitItemsInitialized(false);
    }
  }, [open, kit, mode]);

  // Sync existing items to local draft
  useEffect(() => {
    if (!open || mode !== 'edit' || kitItemsInitialized || kitItemsLoading) return;

    const next: Record<string, KitItemDraft> = {};
    existingKitItems.forEach((entity) => {
      const catId = (entity.entity_data as KitItemEntityData)?.catalog_id;
      if (catId) {
        next[catId] = {
          catalogId: catId,
          quantity: getKitItemQuantity(entity),
          kitItemDisplayName: entity.display_name ?? undefined,
          kitItemIdentifier: entity.entity_identifier ?? undefined,
        };
      }
    });
    setKitItems(next);
    setKitItemsInitialized(true);
  }, [open, mode, kitItemsInitialized, existingKitItems, kitItemsLoading]);

  // --- Computed Data for UI ---

  const catalogLookup = useMemo(() => new Map(catalogItems.map((i) => [i.id, i])), [catalogItems]);
  const catalogTextLookup = useMemo(
    () => new Map(catalogItems.map((i) => [i.id, normalizeCatalogText(i)])),
    [catalogItems],
  );
  const existingCatalogIds = useMemo(
    () =>
      new Set(
        existingKitItems
          .map((e) => (e.entity_data as KitItemEntityData).catalog_id)
          .filter((id): id is string => Boolean(id)),
      ),
    [existingKitItems],
  );

  const isDirty = useMemo(() => {
    if (!open) return false;
    if (mode === 'create') {
      return kitName.trim().length > 0 || Object.keys(kitItems).length > 0;
    }
    if (!kitItemsInitialized) return false;
    const initialName = (kit?.entity_data as KitEntityData)?.name || kit?.display_name || '';
    if (kitName !== initialName) return true;
    const currentIds = new Set(Object.keys(kitItems));
    if (currentIds.size !== existingCatalogIds.size) return true;
    for (const id of currentIds) {
      if (!existingCatalogIds.has(id)) return true;
    }
    const existingQtyMap = new Map(
      existingKitItems.map((e) => [(e.entity_data as KitItemEntityData).catalog_id, e]),
    );
    return Object.keys(kitItems).some((id) => {
      const existing = existingQtyMap.get(id);
      const draft = kitItems[id];
      if (!existing || !draft) return false;
      return getKitItemQuantity(existing) !== draft.quantity;
    });
  }, [
    open,
    mode,
    kitName,
    kitItems,
    kitItemsInitialized,
    kit,
    existingCatalogIds,
    existingKitItems,
  ]);

  const handleRequestClose = useCallback(() => {
    if (isDirty) {
      setConfirmDiscardOpen(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  const handleExport = useCallback(() => {
    const exportRows: IEExportRow[] = Object.values(kitItems).map((draft) => {
      const catalog = catalogLookup.get(draft.catalogId);
      const data = (catalog?.entity_data ?? {}) as Record<string, unknown>;
      return {
        lin: (typeof data.lin === 'string' ? data.lin : '') as string,
        nsn: (typeof data.nsn === 'string' ? data.nsn : '') as string,
        nomenclature: catalog?.display_name ?? '',
        quantity: draft.quantity,
        optional: false,
        notes: '',
      };
    });
    exportIeKitToXlsx(exportRows, kitName || 'ie_kit', ieKitLabel);
  }, [kitItems, catalogLookup, kitName]);

  const handleImportComplete = useCallback(() => {
    // Refresh data after import by invalidating queries
    void queryClient.invalidateQueries({ queryKey: ['entities'] });
  }, [queryClient]);

  const leftItems: SelectionItem[] = useMemo(() => {
    const needle = availableSearch.trim().toLowerCase();
    return catalogItems
      .filter((c) => !kitItems[c.id]) // Filter out selected
      .filter((c) => !needle || multiWordMatch(catalogTextLookup.get(c.id) ?? '', needle))
      .map((c) => ({
        id: c.id,
        primaryText: buildCatalogPrimaryText(c),
        secondaryText: buildCatalogSubtitle(c),
        data: c,
      }));
  }, [catalogItems, kitItems, availableSearch, catalogTextLookup]);

  const rightItems: SelectionItem[] = useMemo(() => {
    const needle = kitSearch.trim().toLowerCase();
    return Object.values(kitItems)
      .map((draft) => {
        const catalog = catalogLookup.get(draft.catalogId);
        return {
          draft,
          catalog,
          searchText: catalogTextLookup.get(draft.catalogId) || '',
        };
      })
      .filter((item) => !needle || multiWordMatch(item.searchText, needle))
      .sort((a, b) => (a.catalog?.display_name || '').localeCompare(b.catalog?.display_name || ''))
      .map(({ draft, catalog }) => ({
        id: draft.catalogId,
        primaryText: buildCatalogPrimaryText(catalog, draft),
        secondaryText: buildCatalogSubtitle(catalog, draft),
        data: draft, // Store draft for quantity access
      }));
  }, [kitItems, kitSearch, catalogLookup, catalogTextLookup]);

  // --- Handlers ---

  const handleAddItem = useCallback((item: SelectionItem) => {
    setKitItems((prev) => ({ ...prev, [item.id]: { catalogId: item.id, quantity: 1 } }));
  }, []);

  const doRemoveItem = useCallback((item: SelectionItem) => {
    setKitItems((prev) => {
      const next = { ...prev };
      delete next[item.id];
      return next;
    });
  }, []);

  const handleRemoveItem = useCallback(
    (item: SelectionItem) => {
      if (mode === 'edit' && existingCatalogIds.has(item.id)) {
        setConfirmRemoveItem(item);
        return;
      }
      doRemoveItem(item);
    },
    [mode, existingCatalogIds, doRemoveItem],
  );

  const handleQuantityChange = useCallback((catalogId: string, val: string) => {
    const qty = Math.max(1, Math.round(Number(val) || 0));
    setKitItems((prev) => ({
      ...prev,
      [catalogId]: { catalogId, quantity: qty },
    }));
  }, []);

  const getAssignedMemberCount = useCallback(async (ieKitId: string): Promise<number> => {
    const assignments = await entityService.list({
      type: 'person_ie_kit',
      filters: {
        'entity_data->>ie_kit_id': `eq.${ieKitId}`,
        status: 'eq.active',
        is_current: 'eq.true',
      },
      limit: 1000,
      order: 'display_name.asc',
    });
    const assignedIds = new Set(
      assignments
        .map((assignment) => (assignment.entity_data as { person_id?: string })?.person_id)
        .filter(Boolean),
    );
    return assignedIds.size;
  }, []);

  // Fetch assigned member count when opening in edit mode
  useEffect(() => {
    if (!open || mode !== 'edit' || !kitId) return;
    let cancelled = false;
    getAssignedMemberCount(kitId).then((count) => {
      if (!cancelled) setAssignedMemberCount(count);
    });
    return () => {
      cancelled = true;
    };
  }, [open, mode, kitId, getAssignedMemberCount]);

  const accessToken = useAuthStore((s) => s.accessToken);
  const actorId = accessToken ? (getActorIdFromToken(accessToken) ?? undefined) : undefined;

  const executeSave = useCallback(async () => {
    setIsSaving(true);
    setErrorMessage(null);

    try {
      // 1. Save Kit Entity
      const payloadData = {
        ...(kit?.entity_data || {}),
        name: kitName.trim(),
        soc_tag: kitRestrictedTag,
      };
      let targetKit = kit;
      let removedAssetsResult: {
        individualsUpdated: number;
        handReceiptsUpdated: number;
      } | null = null;
      let quantityDecreasedResult: {
        individualsUpdated: number;
        handReceiptsUpdated: number;
      } | null = null;
      let addedAssetsResult: {
        individualsUpdated: number;
        handReceiptsUpdated: number;
      } | null = null;
      let addedAssetsImpactCount: number | null = null;

      if (mode === 'create' || !targetKit) {
        const sanitizedName = kitName
          .trim()
          .toUpperCase()
          .replace(/[^A-Z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');
        targetKit = await entityService.create({
          entity_type: 'ie_kit',
          entity_identifier: `${ieKitIdPrefix}-${sanitizedName}`,
          display_name: kitName.trim(),
          entity_data: payloadData,
        });
      } else {
        targetKit = await entityService.update({
          type: 'ie_kit',
          id: targetKit.id,
          patch: { display_name: kitName.trim(), entity_data: payloadData },
        });
      }

      // 2. Diff Items
      const currentIds = new Set(Object.keys(kitItems));
      const existingMap = new Map(
        existingKitItems.map((e) => [(e.entity_data as KitItemEntityData).catalog_id, e]),
      );

      const toCreate = Object.keys(kitItems).filter((id) => !existingMap.has(id));
      const toDelete = Array.from(existingMap.keys()).filter(
        (id): id is string => id !== undefined && !currentIds.has(id),
      );
      const toUpdate = Object.keys(kitItems).filter((id) => {
        const existing = existingMap.get(id);
        const draft = kitItems[id];
        if (!existing || !draft) return false;
        return getKitItemQuantity(existing) !== draft.quantity;
      });

      const quantityDecreaseAdjustments = toUpdate
        .map((id) => {
          const existing = existingMap.get(id);
          const draft = kitItems[id];
          if (!existing || !draft) return null;
          const existingQuantity = getKitItemQuantity(existing);
          return draft.quantity < existingQuantity
            ? { catalogId: id, newQuantity: draft.quantity }
            : null;
        })
        .filter((adjustment): adjustment is { catalogId: string; newQuantity: number } =>
          Boolean(adjustment),
        );

      // 3. Process Changes
      if (toCreate.length) {
        await entityService.createBulkEntities(
          toCreate.map((id) => {
            const draft = kitItems[id]!;
            return {
              entity_type: 'ie_kit_item',
              display_name: `${targetKit!.display_name} • ${catalogLookup.get(id)?.display_name}`,
              entity_data: { ie_kit_id: targetKit!.id, catalog_id: id, quantity: draft.quantity },
            };
          }),
          actorId,
        );
        if (targetKit) {
          addedAssetsImpactCount = await getAssignedMemberCount(targetKit.id);
        }
      }

      if (toDelete.length) {
        await Promise.all(
          toDelete.map((id) => entityService.remove({ id: existingMap.get(id)!.id })),
        );
      }

      if (toUpdate.length) {
        await Promise.all(
          toUpdate.map((id) => {
            const draft = kitItems[id]!;
            return entityService.update({
              type: 'ie_kit_item',
              id: existingMap.get(id)!.id,
              patch: {
                entity_data: { ...existingMap.get(id)!.entity_data, quantity: draft.quantity },
              },
            });
          }),
        );
      }

      if (quantityDecreaseAdjustments.length && targetKit) {
        quantityDecreasedResult = await iemService.updateIeKitQuantityDecreasedAssets({
          ieKitId: targetKit.id,
          adjustments: quantityDecreaseAdjustments,
        });
      }

      if (toDelete.length && targetKit) {
        removedAssetsResult = await iemService.updateIeKitRemovedAssets({
          ieKitId: targetKit.id,
          removedCatalogIds: toDelete,
        });
      }

      if (toCreate.length && targetKit) {
        addedAssetsResult = await iemService.updateIeKitAddedAssets({
          ieKitId: targetKit.id,
          addedCatalogIds: toCreate,
        });
      }

      await queryClient.invalidateQueries({ queryKey: ['entities'] });
      await queryClient.invalidateQueries({ queryKey: ['ie-summary'] });
      if (removedAssetsResult || quantityDecreasedResult || addedAssetsResult) {
        await queryClient.invalidateQueries({ queryKey: ['thr-items'] });
        await queryClient.invalidateQueries({ queryKey: ['phr-items'] });
      }
      notifications.success(`${ieKitLabel} Saved`, `"${kitName}" updated.`);
      if (addedAssetsImpactCount !== null) {
        const label = addedAssetsImpactCount === 1 ? person.lower : person.lowerPlural;
        notifications.info(
          `${person.labelPlural} Impacted`,
          `${addedAssetsImpactCount} ${label} will need their allocations updated.`,
        );
      }
      if (removedAssetsResult) {
        const count = removedAssetsResult.individualsUpdated;
        const label = count === 1 ? person.lower : person.lowerPlural;
        notifications.info(`${person.labelPlural} Updated`, `${count} ${label} updated.`);
      }
      if (quantityDecreasedResult) {
        const count = quantityDecreasedResult.individualsUpdated;
        const label = count === 1 ? person.lower : person.lowerPlural;
        notifications.info(
          `${person.labelPlural} Updated`,
          `${count} ${label} updated due to ${ieKitLabel} quantity decreases.`,
        );
      }
      if (addedAssetsResult && addedAssetsResult.handReceiptsUpdated > 0) {
        const count = addedAssetsResult.individualsUpdated;
        const label = count === 1 ? person.lower : person.lowerPlural;
        notifications.info(
          'Allocations Linked',
          `${addedAssetsResult.handReceiptsUpdated} hand receipt(s) across ${count} ${label} linked to this ${ieKitLabel}.`,
        );
      }
      onSaved?.(targetKit);
      onClose();
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : typeof err === 'object' &&
              err !== null &&
              'message' in err &&
              typeof (err as { message: unknown }).message === 'string'
            ? (err as { message: string }).message
            : 'Save failed.';
      setErrorMessage(msg);
      notifications.error('Error', msg);
    } finally {
      setIsSaving(false);
    }
  }, [
    kitName,
    kitItems,
    kit,
    mode,
    existingKitItems,
    catalogLookup,
    getAssignedMemberCount,
    queryClient,
    notifications,
    onSaved,
    onClose,
  ]);

  const handleSave = useCallback(async () => {
    if (!kitName.trim()) {
      setKitNameTouched(true);
      setErrorMessage(`${ieKitLabel} name is required.`);
      return;
    }
    if (Object.keys(kitItems).length === 0) {
      setErrorMessage('Add at least one item.');
      return;
    }

    // Pre-save warning: in edit mode, if any item quantity is being decreased,
    // warn the user how many assigned individuals may be affected.
    if (mode === 'edit' && kit?.id) {
      const existingMap = new Map(
        existingKitItems.map((e) => [(e.entity_data as KitItemEntityData).catalog_id, e]),
      );
      const hasDecreases = Object.keys(kitItems).some((id) => {
        const existing = existingMap.get(id);
        const draft = kitItems[id];
        return (
          existing !== undefined &&
          draft !== undefined &&
          draft.quantity < getKitItemQuantity(existing)
        );
      });
      if (hasDecreases) {
        setIsSaving(true);
        const count = await getAssignedMemberCount(kit.id).catch(() => 0);
        setIsSaving(false);
        if (count > 0) {
          setQtyDecreaseWarning({ affectedCount: count });
          return;
        }
      }
    }

    await executeSave();
  }, [kitName, kitItems, mode, kit, existingKitItems, getAssignedMemberCount, executeSave]);

  // --- Render Props ---

  const headerContent = (
    <Stack direction="column" gap={2}>
      <Stack direction={{ xs: 'column', md: 'row' }} gap={2} alignItems="center">
        <Typography variant="h5">{ieKitLabel} Name:</Typography>
        <TextField
          size="small"
          value={kitName}
          onChange={(e) => {
            setKitName(e.target.value);
            if (!kitNameTouched) setKitNameTouched(true);
          }}
          onBlur={() => setKitNameTouched(true)}
          placeholder={`e.g., Recon Sustainment ${ieKitLabel}`}
          error={kitNameTouched && !kitName.trim()}
          helperText={kitNameTouched && !kitName.trim() ? 'Required' : undefined}
          sx={{ minWidth: { xs: '100%', md: 360 } }}
        />
        {mode === 'edit' && kitRestrictedTag && (
          <Chip label={restrictedTagLabel} color="warning" size="small" variant="filled" />
        )}
      </Stack>
      <Stack direction="row" alignItems="center" gap={1}>
        <input
          type="checkbox"
          id="restricted-tag-checkbox"
          checked={kitRestrictedTag}
          onChange={(e) => setKitRestrictedTag(e.target.checked)}
          disabled={isViewer || isComptroller || isHr || isSaving}
        />
        <label
          htmlFor="restricted-tag-checkbox"
          style={{
            cursor: isViewer || isComptroller || isHr || isSaving ? 'not-allowed' : 'pointer',
          }}
        >
          {restrictedTagLabel} {ieKitLabel} — visible only to {restrictedTagLabel}-assigned roles
        </label>
      </Stack>
      {mode === 'edit' && assignedMemberCount !== null && assignedMemberCount > 0 && (
        <Alert severity="warning">
          ⚠ This {ieKitLabel} is assigned to {assignedMemberCount}{' '}
          {assignedMemberCount === 1 ? 'member' : 'members'}. Changes will affect their equipment
          allocation.
        </Alert>
      )}
      <Stack direction="row" gap={1}>
        {mode === 'edit' && kitId && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileUploadIcon />}
            onClick={() => setImportDialogOpen(true)}
            disabled={isViewer || isSaving}
          >
            Import from Excel
          </Button>
        )}
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
          disabled={Object.keys(kitItems).length === 0}
        >
          Export Template
        </Button>
      </Stack>
    </Stack>
  );

  const renderQuantityInput = (item: SelectionItem) => (
    <TextField
      label="Qty"
      type="number"
      size="small"
      value={item.data.quantity}
      onChange={(e) => handleQuantityChange(item.id, e.target.value)}
      inputProps={{ min: 1 }}
      sx={{ width: 80 }}
      disabled={isSaving}
    />
  );

  // Warnings
  const missingKitWarning = mode === 'edit' && !kit;

  return (
    <>
      {missingKitWarning && open && <Alert severity="warning">{ieKitLabel} data missing.</Alert>}
      <Dialog
        open={confirmRemoveItem !== null}
        onClose={() => setConfirmRemoveItem(null)}
        aria-labelledby="remove-kit-item-dialog-title"
      >
        <DialogTitle id="remove-kit-item-dialog-title">Remove Kit Item?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This item has assigned assets. Removing it will unlink those assignments. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRemoveItem(null)}>Cancel</Button>
          <Button
            color="error"
            onClick={() => {
              if (confirmRemoveItem) doRemoveItem(confirmRemoveItem);
              setConfirmRemoveItem(null);
            }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={qtyDecreaseWarning !== null}
        onClose={() => setQtyDecreaseWarning(null)}
        aria-labelledby="ieItem-qty-decrease-warning-title"
      >
        <DialogTitle id="ieItem-qty-decrease-warning-title">Confirm Quantity Change</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Reducing item quantities will affect{' '}
            <strong>{qtyDecreaseWarning?.affectedCount ?? 0}</strong>{' '}
            {qtyDecreaseWarning?.affectedCount === 1 ? person.lower : person.lowerPlural} currently
            assigned to this {ieKitLabel}. Assets may be removed from their Hand Receipts.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQtyDecreaseWarning(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={async () => {
              setQtyDecreaseWarning(null);
              await executeSave();
            }}
          >
            Save Anyway
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmDiscardOpen}
        onClose={() => setConfirmDiscardOpen(false)}
        aria-labelledby="discard-ieItem-changes-dialog-title"
      >
        <DialogTitle id="discard-ieItem-changes-dialog-title">Unsaved Changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes. Are you sure you want to close?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDiscardOpen(false)}>Keep Editing</Button>
          <Button
            color="error"
            onClick={() => {
              setConfirmDiscardOpen(false);
              onClose();
            }}
          >
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>
      {kitId && (
        <IeImportDialog
          open={importDialogOpen}
          ieKitId={kitId}
          kitName={kitName || ieKitLabel}
          onClose={() => setImportDialogOpen(false)}
          onImported={handleImportComplete}
        />
      )}
      <DualPaneSelectionDialog
        open={open}
        title={mode === 'edit' ? `Edit ${ieKitLabel}` : `Create ${ieKitLabel}`}
        onClose={handleRequestClose}
        onSave={handleSave}
        isSaving={isSaving}
        saveLabel={`Save ${ieKitLabel}`}
        saveDisabled={
          isViewer ||
          kitName.trim().length === 0 ||
          Object.keys(kitItems).length === 0 ||
          !!missingKitWarning
        }
        errorMessage={errorMessage}
        onClearError={() => setErrorMessage(null)}
        headerContent={headerContent}
        // Left (Available)
        leftTitle="Available Catalog Items"
        leftItems={leftItems}
        leftSearch={availableSearch}
        onLeftSearchChange={setAvailableSearch}
        onLeftItemAdd={handleAddItem}
        leftLoading={catalogLoading}
        leftError={!!catalogError}
        // Right (Selected)
        rightTitle={`${ieKitLabel} Items`}
        rightItems={rightItems}
        rightSearch={kitSearch}
        onRightSearchChange={setKitSearch}
        onRightItemRemove={handleRemoveItem}
        rightLoading={kitItemsLoading}
        renderRightItemExtras={renderQuantityInput}
        rightPageSize={500}
        readOnly={isViewer}
      />
    </>
  );
};
